import callAPI from './call-api';
import { gqlApi as endpoint } from 'utils/settings';
import { getTokens, pickToken } from 'utils/auth';
import { reportError } from 'apis/sentry';

type GiftRegistryError = {
  code: string;
  data?: Object;
};
type ServerResponse<T> = {
  data: T;
  errors?: {
    extensions?: GiftRegistryError;
  }[];
};

const _callApi = function <T>(
  query: string,
  variables?: Object
): Promise<ServerResponse<T>> {
  return getTokens().then((tokens) => {
    const { token } = pickToken(tokens);
    return callAPI({ query, variables, endpoint, token })
      .catch((e) => {
        reportError(e, { context: 'callAPI() catch' });
        return Promise.reject(e);
      })
      .then((response) => response.json())
      .then((res) => {
        if (res.errors) {
          let code = 'INTERNAL_ERROR';
          let data = res.errors;
          if (res.errors && res.errors[0] && res.errors[0].extensions) {
            code = res.errors[0].extensions.code;
            data = res.errors[0].extensions.data;
          } else {
            console.warn('[igift] No error code found!', res);
          }
          const err = new Error(
            `Got ${code} when contacting ${endpoint}. Error data: ${JSON.stringify(
              data
            )}`
          );
          return Promise.reject(err);
        }
        return res;
      });
  });
};

export function addRegistryItem(
  registryId: string | null = null,
  itemNo: string,
  quantity = 1
) {
  return _callApi<{
    addRegistryItems: {
      __typename: string;
      registry: { id: string; title: string };
    };
  }>(
    /* GraphQL */ `
      mutation addRegistryItems($input: AddRegistryItemsInput!) {
        addRegistryItems(input: $input) {
          __typename
          ... on AddRegistryItemsPayload {
            registry {
              id
              title
            }
          }
        }
      }
    `,
    {
      input: {
        registryId,
        items: [{ itemNo, quantity }],
      },
    }
  );
}

export function getActiveRegistry() {
  return _callApi<{
    activeRegistry: { __typename: string; registry: { id: string } };
  }>(/* GraphQL */ `
    query activeRegistry {
      activeRegistry {
        __typename
        ... on ActiveRegistryPayload {
          registry {
            id
          }
        }
      }
    }
  `);
}

export function removeRegistryItem(registryId: string, itemNo: string) {
  return _callApi<{
    removeRegistryItems: {
      __typename: string;
      registry: { id: string; title: string };
    };
  }>(
    /* GraphQL */ `
      mutation removeRegistryItems($input: RemoveRegistryItemsInput!) {
        removeRegistryItems(input: $input) {
          __typename
          ... on RemoveRegistryItemsPayload {
            registry {
              id
              title
            }
          }
        }
      }
    `,
    {
      input: {
        registryId,
        itemNos: [itemNo],
      },
    }
  );
}

export function hasRegistry() {
  return _callApi<{ activeRegistry: { __typename: string } }>(/* GraphQL */ `
    query activeRegistry {
      activeRegistry {
        __typename
      }
    }
  `);
}
